import React, { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { JobShift } from '../../../types/access'
import { useI18n } from '../../../i18n/I18nContext'
import { openDirections } from '../../../services/mapsDirections'
import { requestEphemeralMapPosition, workerMapTiles, type EphemeralMapPosition } from '../../../services/workerJobMap'

type Props = {
  shifts: JobShift[]
  busy?: boolean
  onApply: (shift: JobShift) => void
  onView: (shift: JobShift) => void
  compact?: boolean
}

const point = (shift: JobShift) => shift.mapLocation?.available
  && typeof shift.mapLocation.latitude === 'number'
  && typeof shift.mapLocation.longitude === 'number'
  ? [shift.mapLocation.latitude, shift.mapLocation.longitude] as [number, number]
  : null

export const WorkerJobMap: React.FC<Props> = ({ shifts, busy = false, onApply, onView, compact = false }) => {
  const { t, formatDateTime, formatCurrency } = useI18n()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [position, setPosition] = useState<EphemeralMapPosition | null>(null)
  const [locationState, setLocationState] = useState<'idle' | 'loading' | 'denied' | 'unavailable'>('idle')
  const [tilesFailed, setTilesFailed] = useState(false)
  const visible = useMemo(() => shifts.filter((shift) => point(shift)), [shifts])
  const selected = visible.find((shift) => shift.id === selectedId) || null
  const selectedGroup = selected ? visible.filter((shift) => point(shift)?.join(',') === point(selected)?.join(',')) : []
  const fitKey = visible.map((shift) => `${shift.id}:${point(shift)?.join(',')}`).join('|')

  useEffect(() => {
    if (!visible.some((shift) => shift.id === selectedId)) setSelectedId(visible[0]?.id || null)
  }, [fitKey, selectedId, visible])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true }).setView([45.5, -73.56], 9)
    L.tileLayer(workerMapTiles.url, { attribution: workerMapTiles.attribution, maxZoom: workerMapTiles.maxZoom })
      .on('tileerror', () => setTilesFailed(true))
      .on('load', () => setTilesFailed(false))
      .addTo(map)
    mapRef.current = map
    layerRef.current = L.layerGroup().addTo(map)
    queueMicrotask(() => map.invalidateSize())
    return () => { map.remove(); mapRef.current = null; layerRef.current = null }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (!map || !layer) return
    layer.clearLayers()
    const bounds: L.LatLngExpression[] = []
    visible.forEach((shift) => {
      const coordinates = point(shift)!
      bounds.push(coordinates)
      const approximate = shift.mapLocation?.precision === 'approximate'
      L.circleMarker(coordinates, {
        radius: approximate ? 11 : 9,
        color: approximate ? '#b45309' : '#2563eb',
        fillColor: approximate ? '#f59e0b' : '#2563eb',
        fillOpacity: approximate ? .35 : .8,
        weight: 3,
      }).on('click', () => setSelectedId(shift.id)).bindTooltip(shift.job_title || shift.job_name || t('Open shift')).addTo(layer)
    })
    if (position) {
      const workerPoint: L.LatLngExpression = [position.latitude, position.longitude]
      bounds.push(workerPoint)
      L.circleMarker(workerPoint, { radius: 8, color: '#047857', fillColor: '#10b981', fillOpacity: .9, weight: 3 })
        .bindTooltip(t('My location'))
        .addTo(layer)
      if (position.accuracyMeters) L.circle(workerPoint, { radius: position.accuracyMeters, color: '#10b981', fillOpacity: .05, weight: 1 }).addTo(layer)
    }
    if (bounds.length === 1) map.setView(bounds[0], 13)
    else if (bounds.length > 1) map.fitBounds(L.latLngBounds(bounds), { padding: [32, 32], maxZoom: 14 })
    map.invalidateSize()
  }, [fitKey, position, t])

  const useLocation = async () => {
    setLocationState('loading')
    try {
      setPosition(await requestEphemeralMapPosition())
      setLocationState('idle')
    } catch (error) {
      setLocationState(error instanceof Error && error.message === 'denied' ? 'denied' : 'unavailable')
    }
  }

  if (!visible.length) return <div className="worker-job-map-empty"><strong>{t('No map-visible jobs')}</strong><p>{t('Jobs without a shareable map location remain available in List view.')}</p></div>

  return <section className={`worker-job-map ${compact ? 'worker-job-map--compact' : ''}`} data-testid="worker-job-map">
    <div className="worker-job-map__toolbar">
      <button type="button" className="btn btn-secondary" disabled={locationState === 'loading'} onClick={() => void useLocation()}>{locationState === 'loading' ? t('Finding you…') : t('Use my location')}</button>
      {locationState === 'denied' ? <span role="status">{t('Location permission is off. You can still browse jobs without sharing your location.')}</span> : null}
      {locationState === 'unavailable' ? <span role="status">{t('My location is unavailable. You can still browse jobs.')}</span> : null}
    </div>
    {tilesFailed ? <div className="worker-job-map__error" role="status">{t('Map tiles are unavailable. Job details are still shown below.')} <button type="button" onClick={() => { setTilesFailed(false); mapRef.current?.eachLayer((layer) => { if (layer instanceof L.TileLayer) layer.redraw() }) }}>{t('Retry')}</button></div> : null}
    <div ref={containerRef} className="worker-job-map__canvas" aria-label={t('Job map')} />
    {selected ? <article className="worker-card worker-job-map__preview" data-testid="worker-job-map-preview">
      {selectedGroup.length > 1 ? <div className="worker-job-map__group" aria-label={t('Jobs at this location')}><strong>{t('Jobs at this location')}</strong>{selectedGroup.map((shift) => <button type="button" key={shift.id} aria-pressed={shift.id === selected.id} onClick={() => setSelectedId(shift.id)}>{shift.job_title || shift.job_name || t('Open shift')}</button>)}</div> : null}
      <div className="worker-card-heading"><div><h3>{selected.job_title || selected.job_name || t('Open shift')}</h3><p>{selected.starts_at ? formatDateTime(selected.starts_at) : t('Time pending')}</p></div></div>
      <div className="worker-meta"><span>{selected.company_org_name || selected.agency_org_name || t('Organization')}</span><span>{selected.mapLocation?.displayLabel || selected.location_name || t('Location pending')}</span>{selected.distanceLabel ? <span>{selected.distanceLabel}</span> : null}{selected.resolved_pay_rate != null ? <span>{formatCurrency(Number(selected.resolved_pay_rate), selected.resolved_currency_code || 'CAD')}</span> : null}</div>
      {selected.mapLocation?.precision === 'approximate' ? <p className="worker-guidance">{t('Approximate location. Exact worksite shown after assignment.')}</p> : null}
      <div className="worker-actions"><button type="button" className="btn btn-secondary" onClick={() => onView(selected)}>{t('View Job')}</button><button type="button" className="btn btn-primary" disabled={busy || selected.requestable === false || Boolean(selected.application_status)} onClick={() => onApply(selected)}>{selected.application_status ? t('Applied') : t('Apply')}</button>{selected.mapLocation?.navigationAllowed ? <button type="button" className="btn btn-secondary" onClick={() => openDirections({ name: selected.mapLocation?.displayLabel, latitude: selected.mapLocation?.latitude, longitude: selected.mapLocation?.longitude })}>{t('Directions')}</button> : null}</div>
    </article> : <p className="worker-guidance">{t('Select a marker to preview a job.')}</p>}
  </section>
}
